//
// NOTE: These need to be in their own file to prevent circular dependencies.
//
/**
 * User-accessible in-app features. This is a listing of the features with
 * differential access, based on subscription level (Tier), rather than exhaustive
 * list of all features. It does not include features accessible to all users.
 *
 * NOTE: The values need to work in a URL. They will be user-facing.
 */
export var Feature;
(function (Feature) {
    Feature["ViewRecordLink"] = "view-record-link";
    Feature["VideoClipManage"] = "video-clip-manage";
    Feature["VideoClipDownload"] = "video-clip-download";
    Feature["VideoClipCustomText"] = "video-clip-custom-text";
    Feature["VideoClipDiscard"] = "video-clip-discard";
    Feature["MontageCustomBackgroundColor"] = "montage-custom-bg-color";
    Feature["MontageCustomWatermark"] = "montage-custom-logo";
    Feature["MontageDownload"] = "montage-download";
    Feature["MontageCreate"] = "montage-create";
    Feature["MontageRender"] = "montage-render";
    Feature["GroupVideosRemoveAds"] = "remove-ads";
    Feature["CollaboratorAdd"] = "add-co-organizer";
    // This feature basically just tells us that a user has a Group Videos subscription.
    // It's a simple way to differentiate between users with Shared Albums subscriptions,
    // which is generally treated as though the user doesn't have a subscription in Group
    // Videos. It should only be used if there isn't a more specific Feature.
    Feature["BaseGroupVideosAccess"] = "base-group-videos-access";
    // the user is allowed to edit the subject of the invite sent to contributors
    Feature["ContributorInviteEditSubject"] = "edit-contributor-invite-subject";
    Feature["ContributorInvite"] = "invite-contributor";
    Feature["ContributorSignUpNotRequired"] = "contributor-signup-not-required";
    Feature["RecipientReactions"] = "recipient-reactions";
    //
    // Shared Albums features
    //
    // NOTE: This is different from GroupVideosRemoveAds; it only affects shared
    // albums. We can't use the same feature for Groups Videos because it's a
    // Platinum feature in Group Videos.
    Feature["SharedAlbumsRemoveAds"] = "sa-remove-ads";
    // Video uploads are only available to Preferred subscribers.
    Feature["SharedAlbumsVideos"] = "sa-videos";
    // Each of these features is available at all the Shared Albums tiers, but
    // with different limits. Each feature represents the limits available at
    // the various levels.
    // EXTRA
    Feature["SharedAlbumsStorageLevel1"] = "sa-storage-1";
    // ADVANCED
    Feature["SharedAlbumsStorageLevel2"] = "sa-storage-2";
    // PREFERRED
    Feature["SharedAlbumsStorageLevel3"] = "sa-storage-3";
    // BUSINESS
    Feature["SharedAlbumsStorageLevel4"] = "sa-storage-4";
    Feature["SharedAlbumsAdminsLevel1"] = "sa-admins-1";
    Feature["SharedAlbumsAdminsLevel2"] = "sa-admins-2";
    Feature["SharedAlbumsAdminsLevel3"] = "sa-admins-3";
    // EXTRA
    Feature["SharedAlbumsAlbumsLevel1"] = "sa-albums-1";
    // ADVANCED
    Feature["SharedAlbumsAlbumsLevel2"] = "sa-albums-2";
    // PREFERRED
    Feature["SharedAlbumsAlbumsLevel3"] = "sa-albums-3";
    // BUSINESS
    Feature["SharedAlbumsAlbumsLevel4"] = "sa-albums-4";
})(Feature || (Feature = {}));
export function guardFeature(
/** Note that this is the value of the enum, not the key (e.g.
 * `montage-create` not `MontageCreate`) */
maybeFeatureValue) {
    return Object.values(Feature).find((feature) => feature === maybeFeatureValue);
}
/**
 * Defines the interface for an object that can be queried for access to a
 * particular feature.
 */
export class FeatureAccessTier {
    /**
     * Checks whether this Tier has access to a Feature
     * @param feature the Feature to check access for
     * @returns true if this Tier can access this Feature, false otherwise
     */
    canUseFeature(feature) {
        return this.features.includes(feature);
    }
}
